/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { Box, Button, Chip, Divider, IconButton, Select, Typography, styled } from "@mui/material";

export const ModalContainer = styled(Box)({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "min(1080px, calc(100vw - 32px))",
  maxHeight: "calc(100vh - 40px)",
  backgroundColor: "#ffffff",
  borderRadius: "20px",
  boxShadow: "0px 22px 60px rgba(15, 23, 42, 0.20)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
});

export const HeaderContainer = styled(Box)({
  padding: "22px 28px 16px",
  borderBottom: "1px solid #e5e7eb",
});

export const HeaderTopRow = styled(Box)({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "12px",
});

export const TitleText = styled(Typography)({
  fontSize: "20px",
  fontWeight: 700,
  color: "#111827",
  lineHeight: 1.2,
});

export const SubtitleText = styled(Typography)({
  fontSize: "14px",
  fontWeight: 400,
  color: "#64748b",
  marginTop: "4px",
});

export const StyledCloseButton = styled(IconButton)({
  color: "#6b7280",
  marginTop: "-4px",
});

export const StepperContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "10px",
  marginTop: "18px",
  overflowX: "auto",
  paddingBottom: "4px",
});

export const StepItem = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "10px",
  minWidth: "fit-content",
});

export const StepCircle = styled(Box, {
  shouldForwardProp: (prop) => !["active", "completed"].includes(prop),
})(({ active, completed }) => ({
  width: 28,
  height: 28,
  borderRadius: "999px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "13px",
  fontWeight: 700,
  border: `1px solid ${active || completed ? "#f97316" : "#d1d5db"}`,
  color: active || completed ? "#f97316" : "#94a3b8",
  backgroundColor: active ? "#fff7ed" : "#ffffff",
  flexShrink: 0,
}));

export const StepLabel = styled(Typography, {
  shouldForwardProp: (prop) => !["active"].includes(prop),
})(({ active }) => ({
  fontSize: "14px",
  fontWeight: 600,
  color: active ? "#475569" : "#94a3b8",
  whiteSpace: "nowrap",
}));

export const StepConnector = styled(Box)({
  height: 1,
  minWidth: 80,
  backgroundColor: "#e5e7eb",
  flexShrink: 0,
});

export const BodyContainer = styled(Box)({
  padding: "24px 28px",
  overflowY: "auto",
  flex: 1,
  minHeight: 0,
});

export const UploadDropZone = styled(Box)({
  borderRadius: 18,
  border: "1px dashed #d1d5db",
  minHeight: 250,
  backgroundColor: "#ffffff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "28px 20px",
});

export const UploadPromptContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "12px",
  textAlign: "center",
});

export const UploadIconContainer = styled(Box)({
  width: 72,
  height: 72,
  borderRadius: 16,
  backgroundColor: "#f8fafc",
  color: "#64748b",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 30,
});

export const UploadInstructionText = styled(Typography)({
  fontSize: "14px",
  color: "#475569",
  fontWeight: 600,
});

export const UploadInstructionAccent = styled("span")({
  color: "#0f172a",
});

export const UploadSubText = styled(Typography)({
  fontSize: "14px",
  color: "#94a3b8",
});

export const FileNameText = styled(Typography)({
  fontSize: "14px",
  color: "#334155",
  fontWeight: 600,
});

export const UploadHelperCard = styled(Box)({
  marginTop: "16px",
  padding: "14px 16px",
  borderRadius: "14px",
  backgroundColor: "#f8fafc",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "16px",
  flexWrap: "wrap",
});

export const UploadHelperTextBlock = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "2px",
});

export const UploadHelperTitle = styled(Typography)({
  fontSize: "14px",
  color: "#334155",
  fontWeight: 700,
});

export const UploadHelperSubtitle = styled(Typography)({
  fontSize: "14px",
  color: "#64748b",
});

export const TemplateButton = styled(Button)({
  borderRadius: "10px",
  textTransform: "none",
  color: "#475569",
  borderColor: "#d1d5db",
  fontWeight: 600,
  boxShadow: "none",
});

export const FieldsSection = styled(Box)({
  marginTop: "18px",
});

export const FieldsSectionTitle = styled(Typography)({
  fontSize: "15px",
  color: "#334155",
  fontWeight: 700,
  marginBottom: "12px",
});

export const FieldChipRow = styled(Box)({
  display: "flex",
  gap: "8px",
  flexWrap: "wrap",
});

export const FieldChip = styled(Chip, {
  shouldForwardProp: (prop) => !["requiredfield"].includes(prop),
})(({ requiredfield }) => ({
  borderRadius: "999px",
  backgroundColor: requiredfield ? "#fff7ed" : "#f1f5f9",
  color: requiredfield ? "#ea580c" : "#64748b",
  border: `1px solid ${requiredfield ? "#fdba74" : "#e2e8f0"}`,
  fontWeight: 700,
  height: '35px',
}));

export const RequiredNote = styled(Typography)({
  fontSize: "13px",
  color: "#94a3b8",
  marginTop: "12px",
});

export const FooterDivider = styled(Divider)({
  borderColor: "#e5e7eb",
});

export const FooterContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "16px",
  padding: "18px 28px",
  flexWrap: "wrap",
});

export const FooterLeft = styled(Box)({
  minWidth: 280,
  flex: "1 1 280px",
});

export const FooterActions = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  gap: "12px",
  flex: "1 1 280px",
  flexWrap: "wrap",
});

export const SecondaryButton = styled(Button)({
  textTransform: "none",
  borderRadius: "10px",
  fontWeight: 700,
  color: "#475569",
  borderColor: "#d1d5db",
  minWidth: 116,
});

export const PrimaryButton = styled(Button)({
  textTransform: "none",
  borderRadius: "10px",
  fontWeight: 700,
  minWidth: 136,
  backgroundColor: "#f97316",
  boxShadow: "none",
  '&:hover': {
    backgroundColor: "#ea580c",
    boxShadow: "none",
  },
  '&.Mui-disabled': {
    backgroundColor: "#fed7aa",
    color: "#ffffff",
  },
});

export const MappingMetaRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  flexWrap: "wrap",
  width: "100%",
  marginBottom: "16px",
});

export const FileMetaText = styled(Typography)({
  fontSize: "14px",
  color: "#475569",
  fontWeight: 700,
});

export const MetaChip = styled(Chip)({
  borderRadius: "999px",
  backgroundColor: "#f8fafc",
  border: "1px solid #e2e8f0",
  color: "#64748b",
  fontWeight: 700,
});

export const MappingTable = styled(Box)({
  border: "1px solid #e5e7eb",
  borderRadius: "14px",
  overflow: "hidden",
});

export const MappingHeaderRow = styled(Box)({
  display: "grid",
  gridTemplateColumns: "minmax(180px, 1fr) minmax(260px, 1fr) minmax(220px, 1fr)",
  gap: "16px",
  backgroundColor: "#f8fafc",
  padding: "14px 18px",
  borderBottom: "1px solid #e5e7eb",
  '@media (max-width: 900px)': {
    gridTemplateColumns: "1fr",
  },
});

export const MappingHeaderCell = styled(Typography)({
  fontSize: "14px",
  color: "#64748b",
  fontWeight: 700,
});

export const MappingBody = styled(Box)({
  maxHeight: "420px",
  overflowY: "auto",
});

export const MappingRow = styled(Box, {
  shouldForwardProp: (prop) => !["disabledrow"].includes(prop),
})(({ disabledrow }) => ({
  display: "grid",
  gridTemplateColumns: "minmax(180px, 1fr) minmax(260px, 1fr) minmax(220px, 1fr)",
  gap: "16px",
  padding: "14px 18px",
  borderBottom: "1px solid #eef2f7",
  backgroundColor: disabledrow ? "#f8fafc" : "#ffffff",
  opacity: disabledrow ? 0.72 : 1,
  '@media (max-width: 900px)': {
    gridTemplateColumns: "1fr",
  },
}));

export const MappingCell = styled(Box)({
  display: "flex",
  alignItems: "center",
  minWidth: 0,
});

export const MappingColumnName = styled(Typography)({
  fontSize: "14px",
  color: "#334155",
  fontWeight: 600,
  wordBreak: "break-word",
});

export const MappingSelect = styled(Select)({
  width: "100%",
  backgroundColor: "#ffffff",
  borderRadius: "10px",
  '& .MuiSelect-select': {
    paddingTop: "12px",
    paddingBottom: "12px",
    fontSize: "14px",
  },
});

export const SampleText = styled(Typography)({
  fontSize: "14px",
  color: "#64748b",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

export const SummarySection = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "22px",
});

export const SummaryAlert = styled(Box, {
  shouldForwardProp: (prop) => !["severitytype"].includes(prop),
})(({ severitytype }) => ({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "14px 16px",
  borderRadius: "12px",
  border: `1px solid ${severitytype === "success" ? "#bbf7d0" : "#fde68a"}`,
  backgroundColor: severitytype === "success" ? "#f0fdf4" : "#fffdf2",
}));

export const SummaryAlertText = styled(Typography)({
  fontSize: "14px",
  color: "#475569",
  fontWeight: 600,
});

export const StatusPill = styled(Chip, {
  shouldForwardProp: (prop) => !["statusvalue"].includes(prop),
})(({ statusvalue }) => ({
  borderRadius: "999px",
  fontWeight: 700,
  backgroundColor: statusvalue === "created" ? "#dcfce7" : "#fee2e2",
  color: statusvalue === "created" ? "#15803d" : "#dc2626",
  border: `1px solid ${statusvalue === "created" ? "#86efac" : "#fecaca"}`,
}));
